import { cva } from "class-variance-authority";

// Linear field surface: quiet 1px border-input that darkens on hover, a 1px
// accent outline on focus and no glow. md 32 (default), sm 28. `bare` drops
// the frame for fields that sit inside another surface (composers, popovers);
// `mono` is for codes and schedules.
const fieldBase = "w-full rounded-control border border-border-input bg-bg-surface text-base text-text-primary transition-[border-color] duration-150 hover:border-border-input-hover disabled:cursor-not-allowed disabled:opacity-disabled aria-[invalid=true]:border-red-light aria-[invalid=true]:outline aria-[invalid=true]:outline-1 aria-[invalid=true]:-outline-offset-1 aria-[invalid=true]:outline-red-light";

export const controlSurfaceVariants = cva(
  `${fieldBase} outline-none placeholder:text-text-placeholder focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-accent`,
  {
    variants: {
      size: {
        sm: "h-7 px-2.5",
        md: "h-8 px-3",
      },
      kind: {
        default: "",
        multiline: "!h-auto min-h-[84px] px-3 py-2.5 leading-[19px]",
      },
      variant: {
        default: "",
        bare: "border-0 bg-transparent px-0 hover:border-0 focus-visible:outline-0",
      },
      mono: {
        true: "font-mono",
        false: "",
      },
    },
    defaultVariants: {
      size: "md",
      kind: "default",
      variant: "default",
      mono: false,
    },
  },
);

export const controlSurfaceFrameVariants = cva(
  `${fieldBase} group/control flex items-center gap-2 overflow-hidden focus-within:outline focus-within:outline-1 focus-within:-outline-offset-1 focus-within:outline-accent data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-disabled`,
  {
    variants: {
      size: {
        sm: "h-7 px-2.5",
        md: "h-8 px-3",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

export const controlSurfaceInputClassName = "h-full min-w-0 flex-1 bg-transparent [font-size:inherit] text-text-primary outline-none placeholder:text-text-placeholder file:border-0 file:bg-transparent file:font-medium file:text-text-primary disabled:cursor-not-allowed";

export const controlSurfaceAffixClassName = "flex shrink-0 items-center gap-1.5 text-text-tertiary";

export const controlSurfaceAffixIconClassName = "flex text-text-tertiary [&>svg]:size-3.5";

export const controlSurfaceIconClassName = "size-3.5 shrink-0 text-text-tertiary";

/*
 * Shared focus ring for standalone interactive controls (buttons, checkboxes,
 * radio buttons, switches, badges, tab panels). Inline field controls (inputs,
 * textareas, select triggers) use the ring already embedded in controlSurfaceVariants.
 */
export const focusRingClassName = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2";

// On touch screens every button gets an invisible 44px hit area, so call sites
// never enlarge controls for mobile.
export const touchTargetClassName = "[@media(pointer:coarse)]:before:absolute [@media(pointer:coarse)]:before:left-1/2 [@media(pointer:coarse)]:before:top-1/2 [@media(pointer:coarse)]:before:size-full [@media(pointer:coarse)]:before:min-h-11 [@media(pointer:coarse)]:before:min-w-11 [@media(pointer:coarse)]:before:-translate-x-1/2 [@media(pointer:coarse)]:before:-translate-y-1/2";

// Hidden until the nearest `group` is hovered or focused, or the control
// itself is focused or open.
export const revealOnHoverClassName = "opacity-0 transition-[background-color,color,box-shadow,opacity] group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100";

export const controlChevronClassName = "size-3.5 shrink-0 opacity-50 transition-transform motion-reduce:transition-none";
